// טופס פרמטרים מתת-קבוצה של JSON Schema (11 §1, §6). טהור — נבדק ב-vitest.
// נתמך: string · number/integer · date (format: date) · date-range · enum.
// date-range מזוהה בשלוש צורות:
//   1. { type:'string', format:'date-range' }            → params[name] = { from, to }
//   2. { type:'object', properties:{from:date,to:date} } → params[name] = { from, to }   (z.object({p: z.object({from,to})}))
//   3. זוג ברמה העליונה: from/to או <x>From/<x>To, שניהם date → params.from / params.to   (z.object({from, to}) — הדוגמה ב-09)
// lookup (artifacts-report-lookup, report@3): פרמטר עם `lookup: '<action>'` → בחירה עם חיפוש (LookupField). ערך אחד בלבד — מערך → 'unsupported'.
//   ה-state: הטקסט ב-values[id], מה שנבחר ב-values['<id>.value'] / values['<id>.label'] (lookupKeys); טקסט בלי בחירה → "בחר מהרשימה".
// כל השאר → 'unsupported' (מוצג, והשרת יחליט).

type Json = Record<string, unknown>;
export type Option = { value: string; label: string };

type Base = { id: string; label: string; required: boolean; description?: string };
export type Field =
  | (Base & { kind: 'string'; minLength?: number; maxLength?: number; default?: string })
  | (Base & { kind: 'number'; integer: boolean; min?: number; max?: number; default?: number })
  | (Base & { kind: 'date'; min?: string; max?: string; default?: string })
  | (Base & { kind: 'date-range'; shape: 'object' | 'flat'; fromKey: string; toKey: string; default?: { from?: string; to?: string } })
  | (Base & { kind: 'enum'; options: Option[]; default?: string })
  | (Base & { kind: 'lookup'; action: string; numeric: boolean; default?: string })
  | (Base & { kind: 'unsupported' });

export type Values = Record<string, string>;
export type Errors = Record<string, string>;

const obj = (v: unknown): Json => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {});
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const isDate = (p: Json) => p.type === 'string' && p.format === 'date';
const label = (key: string, p: Json) => str(p.title) ?? str(p.description) ?? key;

const DEFAULT_MSG = {
  required: 'שדה חובה',
  number: 'יש להזין מספר',
  integer: 'יש להזין מספר שלם',
  min: (n: number) => `לפחות ${n}`,
  max: (n: number) => `לכל היותר ${n}`,
  date: 'תאריך לא תקין',
  range: 'תאריך ההתחלה אחרי תאריך הסיום',
  minLen: (n: number) => `לפחות ${n} תווים`,
  maxLen: (n: number) => `לכל היותר ${n} תווים`,
  choice: 'ערך לא מהרשימה',
  pick: 'בחר מהרשימה',
};

function enumOptions(p: Json): Option[] | null {
  if (Array.isArray(p.enum)) {
    const labels = Array.isArray(p['x-enumLabelsHe']) ? (p['x-enumLabelsHe'] as unknown[]) : [];
    return p.enum.map((v, i) => ({ value: String(v), label: str(labels[i]) ?? String(v) }));
  }
  const alts = Array.isArray(p.oneOf) ? p.oneOf : Array.isArray(p.anyOf) ? p.anyOf : null;
  if (alts && alts.every((a) => 'const' in obj(a))) {
    return alts.map((a) => ({ value: String(obj(a).const), label: str(obj(a).title) ?? String(obj(a).const) }));
  }
  return null;
}

function rangePairs(props: Json): Array<{ from: string; to: string; prefix: string }> {
  const out: Array<{ from: string; to: string; prefix: string }> = [];
  for (const k of Object.keys(props)) {
    let prefix: string | null = null;
    if (k === 'from') prefix = '';
    else if (k.endsWith('From')) prefix = k.slice(0, -4);
    else if (k.endsWith('_from')) prefix = k.slice(0, -5);
    if (prefix === null) continue;
    const to = k === 'from' ? 'to' : k.endsWith('From') ? `${prefix}To` : `${prefix}_to`;
    if (isDate(obj(props[k])) && isDate(obj(props[to]))) out.push({ from: k, to, prefix });
  }
  return out;
}

export function parseParamsSchema(schema: unknown): Field[] {
  const s = obj(schema);
  const props = obj(s.properties);
  const required = new Set(Array.isArray(s.required) ? s.required.map(String) : []);
  const pairs = rangePairs(props);
  const consumed = new Set(pairs.flatMap((p) => [p.from, p.to]));
  const fields: Field[] = [];

  for (const key of Object.keys(props)) {
    const pair = pairs.find((p) => p.from === key);
    if (pair) {
      const pf = obj(props[pair.from]);
      const pt = obj(props[pair.to]);
      const t = str(pf.title) && str(pt.title) ? `${str(pf.title)} – ${str(pt.title)}` : 'טווח תאריכים';
      fields.push({
        id: pair.prefix ? `${pair.prefix}Range` : 'range',
        kind: 'date-range',
        shape: 'flat',
        fromKey: pair.from,
        toKey: pair.to,
        label: str(s['x-rangeTitleHe']) ?? t,
        required: required.has(pair.from) || required.has(pair.to),
        default: { from: str(pf.default), to: str(pt.default) },
      });
      continue;
    }
    if (consumed.has(key)) continue;
    const p = obj(props[key]);
    const base = { id: key, label: label(key, p), required: required.has(key), description: str(p.description) };
    const opts = enumOptions(p);
    if (typeof p.lookup === 'string') {
      // בחירה עם חיפוש: ערך אחד (string / number); מערך = בחירה מרובה — לא נתמך
      if (p.type === 'array') fields.push({ ...base, kind: 'unsupported' });
      else fields.push({ ...base, kind: 'lookup', action: p.lookup, numeric: p.type === 'number' || p.type === 'integer', default: p.default === undefined ? undefined : String(p.default) });
    } else if (opts) {
      fields.push({ ...base, kind: 'enum', options: opts, default: p.default === undefined ? undefined : String(p.default) });
    } else if (p.type === 'string' && p.format === 'date-range') {
      const d = obj(p.default);
      fields.push({ ...base, kind: 'date-range', shape: 'object', fromKey: 'from', toKey: 'to', default: { from: str(d.from), to: str(d.to) } });
    } else if (p.type === 'object' && isDate(obj(obj(p.properties).from)) && isDate(obj(obj(p.properties).to))) {
      const d = obj(p.default);
      fields.push({ ...base, kind: 'date-range', shape: 'object', fromKey: 'from', toKey: 'to', default: { from: str(d.from), to: str(d.to) } });
    } else if (isDate(p)) {
      fields.push({ ...base, kind: 'date', min: str(p.formatMinimum) ?? str(p.minimum), max: str(p.formatMaximum) ?? str(p.maximum), default: str(p.default) });
    } else if (p.type === 'number' || p.type === 'integer') {
      fields.push({
        ...base,
        kind: 'number',
        integer: p.type === 'integer',
        min: num(p.minimum) ?? num(p.exclusiveMinimum),
        max: num(p.maximum) ?? num(p.exclusiveMaximum),
        default: num(p.default),
      });
    } else if (p.type === 'string') {
      fields.push({ ...base, kind: 'string', minLength: num(p.minLength), maxLength: num(p.maxLength), default: str(p.default) });
    } else {
      fields.push({ ...base, kind: 'unsupported' });
    }
  }
  return fields;
}

/** מפתחות ה-state של שדה (טווח = שני מפתחות). */
export const rangeKeys = (f: { id: string }) => ({ from: `${f.id}.from`, to: `${f.id}.to` });
/** בחירה עם חיפוש: הטקסט ב-values[id]; הבחירה — value (מה שנשלח) ו-label (מה שהמשתמש ראה). */
export const lookupKeys = (f: { id: string }) => ({ value: `${f.id}.value`, label: `${f.id}.label` });

export function initialValues(fields: Field[]): Values {
  const v: Values = {};
  for (const f of fields) {
    if (f.kind === 'date-range') {
      const k = rangeKeys(f);
      v[k.from] = f.default?.from ?? '';
      v[k.to] = f.default?.to ?? '';
    } else if (f.kind === 'lookup') {
      // בלי ברירת מחדל שנבחרה: המשתמש בוחר מהרשימה (ברירת מחדל בסכימה — בחירה בלי תווית)
      const k = lookupKeys(f);
      v[f.id] = f.default ?? '';
      v[k.value] = f.default ?? '';
      v[k.label] = '';
    } else if (f.kind !== 'unsupported') {
      v[f.id] = f.default === undefined ? '' : String(f.default);
    }
  }
  return v;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (s: string) => DATE_RE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && new Date(`${s}T00:00:00Z`).toISOString().startsWith(s);

export function toParams(
  fields: Field[],
  values: Values,
  msg = DEFAULT_MSG,
): { ok: true; params: Record<string, unknown> } | { ok: false; errors: Errors } {
  const params: Record<string, unknown> = {};
  const errors: Errors = {};
  for (const f of fields) {
    if (f.kind === 'unsupported') continue;
    if (f.kind === 'date-range') {
      const k = rangeKeys(f);
      const from = (values[k.from] ?? '').trim();
      const to = (values[k.to] ?? '').trim();
      if (!from && !to) {
        if (f.required) errors[f.id] = msg.required;
        continue;
      }
      if (!from || !to) {
        errors[f.id] = msg.required;
        continue;
      }
      if (!validDate(from) || !validDate(to)) {
        errors[f.id] = msg.date;
        continue;
      }
      if (from > to) {
        errors[f.id] = msg.range;
        continue;
      }
      if (f.shape === 'object') params[f.id] = { from, to };
      else {
        params[f.fromKey] = from;
        params[f.toKey] = to;
      }
      continue;
    }
    if (f.kind === 'lookup') {
      const picked = values[lookupKeys(f).value] ?? '';
      const text = (values[f.id] ?? '').trim();
      if (picked) params[f.id] = f.numeric ? Number(picked) : picked;
      else if (text) errors[f.id] = msg.pick;
      else if (f.required) errors[f.id] = msg.required;
      continue;
    }
    const raw = (values[f.id] ?? '').trim();
    if (!raw) {
      if (f.required) errors[f.id] = msg.required;
      continue;
    }
    switch (f.kind) {
      case 'string':
        if (f.minLength !== undefined && raw.length < f.minLength) errors[f.id] = msg.minLen(f.minLength);
        else if (f.maxLength !== undefined && raw.length > f.maxLength) errors[f.id] = msg.maxLen(f.maxLength);
        else params[f.id] = raw;
        break;
      case 'number': {
        const n = Number(raw.replace(/,/g, ''));
        if (!Number.isFinite(n)) errors[f.id] = msg.number;
        else if (f.integer && !Number.isInteger(n)) errors[f.id] = msg.integer;
        else if (f.min !== undefined && n < f.min) errors[f.id] = msg.min(f.min);
        else if (f.max !== undefined && n > f.max) errors[f.id] = msg.max(f.max);
        else params[f.id] = n;
        break;
      }
      case 'date':
        if (!validDate(raw)) errors[f.id] = msg.date;
        else params[f.id] = raw;
        break;
      case 'enum':
        if (!f.options.some((o) => o.value === raw)) errors[f.id] = msg.choice;
        else params[f.id] = raw;
        break;
    }
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, params };
}

/** POST …/runs paramLabels: התווית של מה שנבחר בכל שדה בחירה עם חיפוש (רק מה שנבחר; אין → undefined). */
export function toParamLabels(fields: Field[], values: Values): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (f.kind !== 'lookup') continue;
    const k = lookupKeys(f);
    if (values[k.value] && values[k.label]) out[f.id] = values[k.label]!;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * לשונית ריצות / תזמונים: "לקוח: <תווית>" לכל פרמטר עם בחירה עם חיפוש; ריצה בלי תווית (מלפני השדרוג) — הערך עצמו.
 * פרמטרים אחרים לא מוצגים כאן (כמו היום).
 */
export function lookupSummary(schema: unknown, params: unknown, paramLabels: Record<string, string> | null | undefined): string {
  const p = obj(params);
  return parseParamsSchema(schema)
    .flatMap((f) => {
      if (f.kind !== 'lookup' || p[f.id] === undefined || p[f.id] === null || p[f.id] === '') return [];
      return [`${f.label}: ${paramLabels?.[f.id] || String(p[f.id])}`];
    })
    .join(' · ');
}
